import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedService, setSelectedService] = useState<string[]>([]);

  // Handle scroll effect for navbar
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleScroll = () => {
        setScrolled(window.scrollY > 50);
      };
      
      window.addEventListener('scroll', handleScroll);
      
      // Cleanup the event listener on unmount
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const handleServiceToggle = (service: string) => {
    if (selectedService.includes(service)) {
      setSelectedService(selectedService.filter(s => s !== service));
    } else {
      setSelectedService([...selectedService, service]);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/d151086e6762f78a14746affec4c3ce6/b585659f57d4c05d35852bffd0a5a827.jpeg" 
                alt="The Boat Babysitter" 
                className="h-20 md:h-32 w-auto"
                style={{ backgroundColor: 'white' }}
              />
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-base font-bold text-[#004E7C] hover:text-[#FF6B35] transition-colors whitespace-nowrap cursor-pointer">Services</a>
              <span className="text-gray-300">•</span>
              <a href="#pricing" className="text-base font-bold text-[#004E7C] hover:text-[#FF6B35] transition-colors whitespace-nowrap cursor-pointer">Pricing</a>
              <span className="text-gray-300">•</span>
              <a href="#how-it-works" className="text-base font-bold text-[#004E7C] hover:text-[#FF6B35] transition-colors whitespace-nowrap cursor-pointer">How It Works</a>
              <span className="text-gray-300">•</span>
              <a href="#storage" className="text-base font-bold text-[#004E7C] hover:text-[#FF6B35] transition-colors whitespace-nowrap cursor-pointer">Storage</a>
              <span className="text-gray-300">•</span>
              <a href="#contact" className="text-base font-bold text-[#004E7C] hover:text-[#FF6B35] transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            </div>

            <a href="tel:3237190661" className="hidden md:block text-base font-bold text-[#FF6B35] whitespace-nowrap cursor-pointer">
              Emergency: (323) 719-0661
            </a>

            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-menu-line text-2xl text-[#004E7C]"></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-6 py-4 space-y-3">
              <a href="#services" className="block text-base font-bold text-gray-700 hover:text-[#FF6B35] cursor-pointer">Services</a>
              <a href="#pricing" className="block text-base font-bold text-gray-700 hover:text-[#FF6B35] cursor-pointer">Pricing</a>
              <a href="#how-it-works" className="block text-base font-bold text-gray-700 hover:text-[#FF6B35] cursor-pointer">How It Works</a>
              <a href="#storage" className="block text-base font-bold text-gray-700 hover:text-[#FF6B35] cursor-pointer">Storage</a>
              <a href="#contact" className="block text-base font-bold text-gray-700 hover:text-[#FF6B35] cursor-pointer">Contact</a>
              <a href="tel:3237190661" className="block text-base font-bold text-[#FF6B35] cursor-pointer">Emergency: (323) 719-0661</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-24 md:pt-0">
        {/* Animated Background */}
        <div className="absolute inset-0">
          {/* Main Background Image */}
          <img 
            src="https://static.readdy.ai/image/d151086e6762f78a14746affec4c3ce6/fa63e999356d9b8203ddd94dfee16212.jpeg"
            alt="Marina Del Rey at sunset"
            className="w-full h-full object-cover object-top"
          />
          
          {/* Animated Clouds Layer */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-0 w-full h-full animate-cloud-slow">
              <div className="absolute top-[10%] left-[-10%] w-[300px] h-[100px] bg-white/20 rounded-full blur-3xl"></div>
              <div className="absolute top-[20%] right-[-5%] w-[400px] h-[120px] bg-white/15 rounded-full blur-3xl"></div>
            </div>
            <div className="absolute top-0 left-0 w-full h-full animate-cloud-medium">
              <div className="absolute top-[15%] left-[20%] w-[350px] h-[110px] bg-white/25 rounded-full blur-3xl"></div>
              <div className="absolute top-[25%] right-[15%] w-[280px] h-[90px] bg-white/20 rounded-full blur-3xl"></div>
            </div>
            <div className="absolute top-0 left-0 w-full h-full animate-cloud-fast">
              <div className="absolute top-[8%] left-[40%] w-[320px] h-[95px] bg-white/18 rounded-full blur-3xl"></div>
            </div>
          </div>

          {/* Animated Sun Rays */}
          <div className="absolute top-[15%] right-[20%] w-[200px] h-[200px]">
            {/* Sun glow */}
            <div className="absolute inset-0 bg-yellow-200/40 rounded-full blur-3xl animate-sun-pulse"></div>
            {/* Rotating rays */}
            <div className="absolute inset-0 animate-sun-rotate">
              <div className="absolute top-1/2 left-1/2 w-[300px] h-[2px] bg-gradient-to-r from-transparent via-yellow-200/30 to-transparent -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute top-1/2 left-1/2 w-[300px] h-[2px] bg-gradient-to-r from-transparent via-yellow-200/30 to-transparent -translate-x-1/2 -translate-y-1/2 rotate-45"></div>
              <div className="absolute top-1/2 left-1/2 w-[300px] h-[2px] bg-gradient-to-r from-transparent via-yellow-200/30 to-transparent -translate-x-1/2 -translate-y-1/2 rotate-90"></div>
              <div className="absolute top-1/2 left-1/2 w-[300px] h-[2px] bg-gradient-to-r from-transparent via-yellow-200/30 to-transparent -translate-x-1/2 -translate-y-1/2 rotate-[135deg]"></div>
            </div>
            {/* Sparkle effects */}
            <div className="absolute top-0 right-0 w-2 h-2 bg-white rounded-full animate-sparkle-1"></div>
            <div className="absolute bottom-0 left-0 w-2 h-2 bg-white rounded-full animate-sparkle-2"></div>
            <div className="absolute top-1/2 right-[-20px] w-2 h-2 bg-white rounded-full animate-sparkle-3"></div>
          </div>

          {/* Subtle shimmer on water */}
          <div className="absolute bottom-0 left-0 right-0 h-1/2">
            <div className="absolute inset-0 bg-gradient-to-t from-white/5 to-transparent animate-water-shimmer"></div>
          </div>

          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
              Premium boat care – because your baby deserves better.
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 leading-relaxed">
              Professional boat care, monitoring, and concierge services while you're away
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="#contact" 
                className="px-6 sm:px-8 py-3 sm:py-4 bg-[#FF6B35] text-white font-semibold rounded-full hover:bg-[#E55A25] transition-all shadow-lg whitespace-nowrap cursor-pointer inline-flex items-center text-sm sm:text-base"
              >
                Get Started <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a 
                href="sms:3237190661" 
                className="px-6 sm:px-8 py-3 sm:py-4 bg-[#DC2626] text-white font-semibold rounded-full hover:bg-[#B91C1C] transition-all whitespace-nowrap cursor-pointer inline-flex items-center text-sm sm:text-base"
              >
                <i className="ri-phone-line mr-2"></i> Emergency Response
              </a>
            </div>
            <p className="mt-8 text-white/70 text-sm">Located in Marina Del Rey</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-4">
              <span className="w-2 h-2 bg-[#FF6B35] rounded-full mr-3"></span>
              <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">What We Do</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
              Complete Boat Care<br />While You're Away
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-[#F5F3EF] rounded-3xl p-8 hover:shadow-xl transition-all cursor-pointer group">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-anchor-line text-4xl text-[#004E7C]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Weekly Inspections</h3>
              <ul className="space-y-3 text-gray-600 leading-relaxed">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Bilge, pumps, shore power checks</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Lines, fenders, chafe inspection</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Battery voltage tests</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Photo + video report every visit</span>
                </li>
              </ul>
            </div>

            {/* Service Card 2 - Featured */}
            <div className="bg-[#F5F3EF] rounded-3xl p-8 hover:shadow-xl transition-all cursor-pointer border-2 border-[#FF6B35] group">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-gift-line text-4xl text-[#004E7C]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Pre-Arrival Prep</h3>
              <ul className="space-y-3 text-gray-600 leading-relaxed">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Grocery stocking & provisioning</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Cabin airing & climate control</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Fresh linens & towels</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Systems powered & ready</span>
                </li>
              </ul>
            </div>

            {/* Service Card 3 */}
            <div className="bg-[#F5F3EF] rounded-3xl p-8 hover:shadow-xl transition-all cursor-pointer group">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-refresh-line text-4xl text-[#004E7C]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Post-Use Reset</h3>
              <ul className="space-y-3 text-gray-600 leading-relaxed">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Light cleaning & trash removal</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Surfaces wiped & heads reset</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Hoses stowed properly</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Systems powered down safely</span>
                </li>
              </ul>
            </div>

            {/* Service Card 4 */}
            <div className="bg-[#F5F3EF] rounded-3xl p-8 hover:shadow-xl transition-all cursor-pointer group">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-tools-line text-4xl text-[#004E7C]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Maintenance Coordination</h3>
              <ul className="space-y-3 text-gray-600 leading-relaxed">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Meet & supervise repair vendors</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Get quotes & schedule work</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Verify completion with photos</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Handle warranty follow-ups</span>
                </li>
              </ul>
            </div>

            {/* Service Card 5 */}
            <div className="bg-[#F5F3EF] rounded-3xl p-8 hover:shadow-xl transition-all cursor-pointer group">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-4xl text-[#004E7C]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Emergency Response</h3>
              <ul className="space-y-3 text-gray-600 leading-relaxed">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>24/7 storm prep & line doubling</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Alarm & power failure response</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Water intrusion handling</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Average response: 30 minutes</span>
                </li>
              </ul>
            </div>

            {/* Service Card 6 */}
            <div className="bg-[#F5F3EF] rounded-3xl p-8 hover:shadow-xl transition-all cursor-pointer group">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-archive-line text-4xl text-[#004E7C]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Secure Storage</h3>
              <ul className="space-y-3 text-gray-600 leading-relaxed">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Climate-controlled garage space</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Cushions, sails, equipment</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>Tools & outboard engines</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-1"></i>
                  <span>High-value items welcome</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-start justify-between mb-16">
            <div>
              <p className="text-sm text-gray-500 uppercase tracking-wider mb-4">The Process</p>
              <h2 className="text-5xl md:text-6xl font-serif italic text-gray-900">
                Simple, Reliable, Transparent
              </h2>
            </div>
            <div className="lg:w-1/2 mt-6 lg:mt-0">
              <p className="text-lg text-gray-600 leading-relaxed">
                We treat your boat like our own. Every visit is documented, every issue is addressed, and you're always in the loop. No surprises, just peace of mind.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {/* Step 1 */}
            <div className="bg-[#FF6B35] rounded-3xl p-10 relative">
              <div className="absolute top-10 right-10 w-12 h-12 bg-white rounded-full flex items-center justify-center cursor-pointer">
                <i className="ri-arrow-right-up-line text-xl text-[#FF6B35]"></i>
              </div>
              <div className="text-8xl font-serif text-white/30 mb-6">01</div>
              <h3 className="text-3xl font-bold text-white mb-4">Book Your Service</h3>
              <p className="text-white/90 text-lg leading-relaxed">
                Choose your package or request custom care tailored to your boat's needs
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-[#9B7EBD] rounded-3xl p-10 relative">
              <div className="absolute top-10 right-10 w-12 h-12 bg-white rounded-full flex items-center justify-center cursor-pointer">
                <i className="ri-arrow-right-up-line text-xl text-[#9B7EBD]"></i>
              </div>
              <div className="text-8xl font-serif text-white/30 mb-6">02</div>
              <h3 className="text-3xl font-bold text-white mb-4">We Visit & Report</h3>
              <p className="text-white/90 text-lg leading-relaxed">
                Photo and video documentation after every check, sent directly to you
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-[#F4D35E] rounded-3xl p-10 relative">
              <div className="absolute top-10 right-10 w-12 h-12 bg-white rounded-full flex items-center justify-center cursor-pointer">
                <i className="ri-arrow-right-up-line text-xl text-[#F4D35E]"></i>
              </div>
              <div className="text-8xl font-serif text-white/30 mb-6">03</div>
              <h3 className="text-3xl font-bold text-white mb-4">You Relax</h3>
              <p className="text-white/90 text-lg leading-relaxed">
                Real-time updates and emergency response when needed. Your boat is in good hands
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-gray-500 uppercase tracking-wider mb-4">Pricing</p>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900">
              Plans That Fit<br />Your Needs
            </h2>
            <p className="text-lg text-gray-600 mt-6">Custom options available — because not all boats (or owners) are the same</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-start">
            {/* The Essentials */}
            <div className="bg-[#F8F8F8] rounded-3xl p-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">The Essentials</h3>
              <div className="mb-8">
                <span className="text-6xl font-serif font-bold text-gray-900">$400</span>
                <span className="text-lg text-gray-500">/month</span>
              </div>
              <div className="h-px bg-gray-300 mb-8"></div>
              <p className="text-sm font-semibold text-gray-900 mb-4">You get:</p>
              <ul className="space-y-4 mb-10">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Weekly check-ins</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Clean, photo-rich reports</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Systems health check</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Bilge, battery, lines, fenders, interior</span>
                </li>
              </ul>
              <a href="#contact" className="block w-full py-4 border-2 border-gray-900 text-gray-900 font-semibold rounded-full hover:bg-gray-900 hover:text-white transition-all text-center whitespace-nowrap cursor-pointer">
                Get Started
              </a>
            </div>

            {/* The VIP Care Plan - Featured */}
            <div className="bg-white rounded-3xl p-10 shadow-2xl relative md:-mt-6">
              <div className="absolute -top-4 right-8 bg-[#FF6B35] text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-wider">
                Most Popular
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">The VIP Care Plan</h3>
              <div className="mb-8">
                <span className="text-6xl font-serif font-bold text-gray-900">$600</span>
                <span className="text-lg text-gray-500">/month</span>
              </div>
              <div className="h-px bg-gray-300 mb-8"></div>
              <p className="text-sm font-semibold text-gray-900 mb-4">You get:</p>
              <ul className="space-y-4 mb-10">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Twice-weekly visits</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Light post-use resets</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Monthly engine run-up</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Water fill, battery conditioning</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Vendor scheduling made painless</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Towels and blankets laundered and returned</span>
                </li>
              </ul>
              <a href="#contact" className="block w-full py-4 bg-[#FF6B35] text-white font-semibold rounded-full hover:bg-[#E55A25] transition-all text-center whitespace-nowrap cursor-pointer">
                Get Started
              </a>
            </div>

            {/* The Total Takeover */}
            <div className="bg-[#FFF8F0] rounded-3xl p-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">The Total Takeover</h3>
              <div className="mb-8">
                <span className="text-6xl font-serif font-bold text-gray-900">$1,000</span>
                <span className="text-lg text-gray-500">/month</span>
              </div>
              <div className="h-px bg-gray-300 mb-8"></div>
              <p className="text-sm font-semibold text-gray-900 mb-4">You get:</p>
              <ul className="space-y-4 mb-10">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Three visits a week</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Full pre-arrival setup (lights, fridge, AC, groceries, ice — the works)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Full post-use reset</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Vendor coordination and supervision</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Monthly "state of the vessel" report</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Emergency calls included</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-3 mt-1"></i>
                  <span className="text-gray-700">Towels and blankets laundered and returned</span>
                </li>
              </ul>
              <a href="#contact" className="block w-full py-4 border-2 border-gray-900 text-gray-900 font-semibold rounded-full hover:bg-gray-900 hover:text-white transition-all text-center whitespace-nowrap cursor-pointer">
                Get Started
              </a>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 italic mb-4">All plans include emergency response</p>
            <a href="#add-ons" className="text-[#FF6B35] font-semibold hover:underline cursor-pointer">
              View À La Carte Services <i className="ri-arrow-right-line ml-1"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Storage Section */}
      <section id="storage" className="py-0 bg-white">
        <div className="grid md:grid-cols-2">
          {/* Left Content */}
          <div className="bg-[#004E7C] p-16 lg:p-24 flex items-center">
            <div>
              <div className="flex items-center mb-6">
                <span className="w-2 h-2 bg-[#FF6B35] rounded-full mr-3"></span>
                <span className="text-sm font-semibold text-[#FF6B35] uppercase tracking-wider">Bonus Service</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Secure Gear Storage
              </h2>
              <p className="text-xl text-white/70 mb-10">
                Your marina doesn't offer this. We do.
              </p>
              <div className="space-y-6 mb-10">
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center mr-4">
                    <i className="ri-home-gear-line text-2xl text-[#FF6B35]"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-1">Climate-Controlled Space</h4>
                    <p className="text-white/70">Protected garage storage for all your gear</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center mr-4">
                    <i className="ri-ship-line text-2xl text-[#FF6B35]"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-1">Everything Welcome</h4>
                    <p className="text-white/70">Cushions, sails, equipment, tools, outboards</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center mr-4">
                    <i className="ri-price-tag-3-line text-2xl text-[#FF6B35]"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-1">Affordable Rates</h4>
                    <p className="text-white/70">$75-100/month, high-value items welcome</p>
                  </div>
                </div>
              </div>
              <a href="#contact" className="inline-block px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-[#004E7C] transition-all whitespace-nowrap cursor-pointer">
                Learn More About Storage
              </a>
            </div>
          </div>

          {/* Right Image */}
          <div className="h-full min-h-[600px]">
            <img 
              src="https://public.readdy.ai/ai/img_res/d3dd1362e765e31fae028c328e0de90f.jpg"
              alt="Organized boat gear storage"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Add-Ons Section */}
      <section id="add-ons" className="py-24 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              À La Carte Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Need something specific? We offer flexible add-on services to complement your care package
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Add-on 1 */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between mb-4">
                <h4 className="text-xl font-bold text-gray-900">Pre-Arrival Prep</h4>
                <span className="text-2xl font-bold text-[#FF6B35]">$175</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">Full concierge service with stocking and setup</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Grocery stocking</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Climate control</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Fresh linens</span>
                </li>
              </ul>
            </div>

            {/* Add-on 2 */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between mb-4">
                <h4 className="text-xl font-bold text-gray-900">Post-Use Reset</h4>
                <span className="text-2xl font-bold text-[#FF6B35]">$120</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">Complete cleanup after your trip</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Light cleaning</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Trash removal</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Systems shutdown</span>
                </li>
              </ul>
            </div>

            {/* Add-on 3 */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between mb-4">
                <h4 className="text-xl font-bold text-gray-900">Vendor Supervision</h4>
                <span className="text-2xl font-bold text-[#FF6B35]">$60/hr</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">We meet repair people for you</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Unlock & supervise</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Photo documentation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Verify completion</span>
                </li>
              </ul>
            </div>

            {/* Add-on 4 */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between mb-4">
                <h4 className="text-xl font-bold text-gray-900">Emergency Call-Out</h4>
                <span className="text-2xl font-bold text-[#FF6B35]">$150</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">Urgent response + $75/hr on-site</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Storm preparation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Alarm response</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Water intrusion</span>
                </li>
              </ul>
            </div>

            {/* Add-on 5 */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between mb-4">
                <h4 className="text-xl font-bold text-gray-900">Engine Run-Up</h4>
                <span className="text-2xl font-bold text-[#FF6B35]">$65</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">Monthly engine maintenance</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Start & warm engines</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Battery check</span>
                </li>
              </ul>
            </div>

            {/* Add-on 6 */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between mb-4">
                <h4 className="text-xl font-bold text-gray-900">Fuel Run</h4>
                <span className="text-2xl font-bold text-[#FF6B35]">$65</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">Coordination & delivery</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Schedule fueling</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Verify completion</span>
                </li>
              </ul>
            </div>

            {/* Add-on 7 */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between mb-4">
                <h4 className="text-xl font-bold text-gray-900">Parts Pickup</h4>
                <span className="text-2xl font-bold text-[#FF6B35]">$60</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">West Marine, Costco, FedEx runs</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Pickup & delivery</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-[#FF6B35] mr-2 mt-0.5 text-xs"></i>
                  <span>Photo confirmation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#FFF8F0]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <div className="flex items-center mb-4">
              <span className="w-2 h-2 bg-[#FF6B35] rounded-full mr-3"></span>
              <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Testimonials</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900">
              Trusted by Marina Del Rey<br />Boat Owners
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="flex mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-[#FF6B35]"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                "Complete peace of mind"
              </h4>
              <p className="text-gray-600 leading-relaxed mb-6">
                I travel for work constantly and was always worried about my boat. The Boat Babysitter sends me detailed reports after every visit. I can finally relax knowing someone reliable is watching over her.
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#004E7C] rounded-full flex items-center justify-center text-white font-bold mr-3">
                  JM
                </div>
                <div>
                  <p className="font-semibold text-gray-900">James Mitchell</p>
                  <p className="text-sm text-gray-500">Boat Owner, MDR</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="flex mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-[#FF6B35]"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                "Saved me during a storm"
              </h4>
              <p className="text-gray-600 leading-relaxed mb-6">
                When that unexpected storm hit last month, they were at my boat within 30 minutes doubling lines and securing everything. That emergency response alone was worth every penny of the annual service.
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#004E7C] rounded-full flex items-center justify-center text-white font-bold mr-3">
                  SC
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Sarah Chen</p>
                  <p className="text-sm text-gray-500">Boat Owner, MDR</p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="flex mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-[#FF6B35]"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                "Pre-arrival service is amazing"
              </h4>
              <p className="text-gray-600 leading-relaxed mb-6">
                We use the pre-arrival prep every time we come down from LA. Walking onto a fully stocked, climate-controlled boat with fresh linens feels like checking into a five-star hotel. Absolutely worth it.
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#004E7C] rounded-full flex items-center justify-center text-white font-bold mr-3">
                  DP
                </div>
                <div>
                  <p className="font-semibold text-gray-900">David & Patricia Rodriguez</p>
                  <p className="text-sm text-gray-500">Boat Owners, MDR</p>
                </div>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="flex mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-[#FF6B35]"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                "They handle everything"
              </h4>
              <p className="text-gray-600 leading-relaxed mb-6">
                From coordinating with my mechanic to picking up parts from West Marine, they've become my boat's personal assistant. I don't have to take time off work anymore to meet vendors. Game changer.
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#004E7C] rounded-full flex items-center justify-center text-white font-bold mr-3">
                  MK
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Michael Kowalski</p>
                  <p className="text-sm text-gray-500">Boat Owner, MDR</p>
                </div>
              </div>
            </div>

            {/* Testimonial 5 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="flex mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-[#FF6B35]"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                "Storage solution is brilliant"
              </h4>
              <p className="text-gray-600 leading-relaxed mb-6">
                The marina has zero storage options. Being able to store our cushions, sails, and seasonal gear in their climate-controlled garage has been a lifesaver. Everything stays in perfect condition.
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#004E7C] rounded-full flex items-center justify-center text-white font-bold mr-3">
                  LT
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Lisa Thompson</p>
                  <p className="text-sm text-gray-500">Boat Owner, MDR</p>
                </div>
              </div>
            </div>

            {/* Testimonial 6 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="flex mb-4">
                {[1,2,3,4,5].map((star) => (
                  <i key={star} className="ri-star-fill text-[#FF6B35]"></i>
                ))}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                "Professional and reliable"
              </h4>
              <p className="text-gray-600 leading-relaxed mb-6">
                After trying two other services that were inconsistent, I found The Boat Babysitter. They show up every week like clockwork, send thorough reports, and actually care about my boat. Highly recommend.
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#004E7C] rounded-full flex items-center justify-center text-white font-bold mr-3">
                  RH
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Robert Harrison</p>
                  <p className="text-sm text-gray-500">Boat Owner, MDR</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency CTA Section */}
      <section className="py-32 bg-gradient-to-br from-[#FF6B35] to-[#E55A25]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Boat Emergency?<br />We're On It.
          </h2>
          <p className="text-xl text-white/90 mb-10">
            24/7 response for storm prep, alarms, and urgent issues
          </p>
          <a 
            href="tel:3237190661" 
            className="inline-flex items-center px-10 py-5 bg-white text-[#004E7C] font-bold text-lg rounded-full hover:bg-gray-100 transition-all shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-3 text-xl"></i>
            Call Now: (323) 719-0661
          </a>
          <p className="mt-6 text-white/70 text-sm">Average response time: 30 minutes</p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Left Content */}
            <div className="lg:col-span-2">
              <p className="text-sm text-gray-500 uppercase tracking-wider mb-4">Get In Touch</p>
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Let's Talk About<br />Your Boat
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Every boat is different, and so are your needs. We offer custom packages and consultations to ensure your vessel gets exactly the care it deserves.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-phone-line text-2xl text-[#FF6B35] mr-4 mt-1"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Phone</p>
                    <a href="tel:3237190661" className="text-gray-600 hover:text-[#FF6B35] cursor-pointer">(323) 719-0661</a>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-mail-line text-2xl text-[#FF6B35] mr-4 mt-1"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Email</p>
                    <a href="mailto:TheBoatBabysitter@gmail.com" className="text-gray-600 hover:text-[#FF6B35] cursor-pointer">TheBoatBabysitter@gmail.com</a>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-map-pin-line text-2xl text-[#FF6B35] mr-4 mt-1"></i>
                  <div>
                    <p className="font-semibold text-gray-900">Location</p>
                    <p className="text-gray-600">Marina Del Rey, CA<br />Serving all MDR slips</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Form */}
            <div className="lg:col-span-3">
              <form 
                id="contact_form"
                data-readdy-form
                action="https://readdy.ai/api/form/d4npbif2prpfnnorm4s0"
                method="POST"
                className="space-y-8"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  
                  // Get selected services
                  const services: string[] = [];
                  formData.getAll('service_interest').forEach(service => {
                    services.push(service.toString());
                  });
                  
                  // Create URL encoded data
                  const data = new URLSearchParams();
                  data.append('name', formData.get('name') as string);
                  data.append('email', formData.get('email') as string);
                  data.append('phone', formData.get('phone') as string);
                  data.append('boat_location', formData.get('boat_location') as string);
                  data.append('service_interest', services.join(', '));
                  data.append('message', formData.get('message') as string);

                  fetch('https://readdy.ai/api/form/d4npbif2prpfnnorm4s0', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: data.toString()
                  })
                  .then(response => {
                    if (response.ok) {
                      alert('Thank you! We\'ll be in touch soon.');
                      form.reset();
                      setSelectedService([]);
                    } else {
                      alert('Something went wrong. Please try again.');
                    }
                  })
                  .catch(() => {
                    alert('Something went wrong. Please try again.');
                  });
                }}
              >
                <div>
                  <input 
                    type="text" 
                    name="name"
                    placeholder="Your Name"
                    required
                    className="w-full pb-4 border-b-2 border-gray-300 focus:border-[#FF6B35] outline-none text-lg transition-colors"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <input 
                      type="email" 
                      name="email"
                      placeholder="Email Address"
                      required
                      className="w-full pb-4 border-b-2 border-gray-300 focus:border-[#FF6B35] outline-none text-lg transition-colors"
                    />
                  </div>
                  <div>
                    <input 
                      type="tel" 
                      name="phone"
                      placeholder="Phone Number"
                      required
                      className="w-full pb-4 border-b-2 border-gray-300 focus:border-[#FF6B35] outline-none text-lg transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <select 
                    name="boat_location"
                    required
                    className="w-full pb-4 border-b-2 border-gray-300 focus:border-[#FF6B35] outline-none text-lg transition-colors cursor-pointer"
                  >
                    <option value="">Boat Location in MDR</option>
                    <option value="Basin A">Basin A</option>
                    <option value="Basin B">Basin B</option>
                    <option value="Basin C">Basin C</option>
                    <option value="Basin D">Basin D</option>
                    <option value="Basin E">Basin E</option>
                    <option value="Basin F">Basin F</option>
                    <option value="Basin G">Basin G</option>
                    <option value="Basin H">Basin H</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-4">Service Interest (select all that apply)</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        name="service_interest"
                        value="Weekly Inspections"
                        checked={selectedService.includes('Weekly Inspections')}
                        onChange={() => handleServiceToggle('Weekly Inspections')}
                        className="w-5 h-5 text-[#FF6B35] border-gray-300 rounded focus:ring-[#FF6B35] cursor-pointer"
                      />
                      <span className="ml-3 text-gray-700">Weekly Inspections</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        name="service_interest"
                        value="Pre-Arrival Prep"
                        checked={selectedService.includes('Pre-Arrival Prep')}
                        onChange={() => handleServiceToggle('Pre-Arrival Prep')}
                        className="w-5 h-5 text-[#FF6B35] border-gray-300 rounded focus:ring-[#FF6B35] cursor-pointer"
                      />
                      <span className="ml-3 text-gray-700">Pre-Arrival Prep</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        name="service_interest"
                        value="Emergency Response"
                        checked={selectedService.includes('Emergency Response')}
                        onChange={() => handleServiceToggle('Emergency Response')}
                        className="w-5 h-5 text-[#FF6B35] border-gray-300 rounded focus:ring-[#FF6B35] cursor-pointer"
                      />
                      <span className="ml-3 text-gray-700">Emergency Response</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        name="service_interest"
                        value="Storage"
                        checked={selectedService.includes('Storage')}
                        onChange={() => handleServiceToggle('Storage')}
                        className="w-5 h-5 text-[#FF6B35] border-gray-300 rounded focus:ring-[#FF6B35] cursor-pointer"
                      />
                      <span className="ml-3 text-gray-700">Storage</span>
                    </label>
                  </div>
                </div>

                <div>
                  <textarea 
                    name="message"
                    placeholder="Tell us about your boat and what you need..."
                    rows={4}
                    maxLength={500}
                    className="w-full pb-4 border-b-2 border-gray-300 focus:border-[#FF6B35] outline-none text-lg transition-colors resize-none"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-2">Maximum 500 characters</p>
                </div>

                <button 
                  type="submit"
                  className="px-10 py-4 bg-[#FF6B35] text-white font-semibold rounded-full hover:bg-[#E55A25] transition-all shadow-lg whitespace-nowrap cursor-pointer inline-flex items-center"
                >
                  Request Consultation <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* About Me Section */}
      <section id="about" className="py-24 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Image */}
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautiful%20Marina%20Del%20Rey%20harbor%20at%20golden%20hour%20with%20boats%20docked%20at%20marina%20peaceful%20water%20reflections%20modern%20yacht%20club%20professional%20nautical%20scene%20warm%20natural%20lighting%20no%20people%20empty%20dock%20scenic%20california%20marina%20background&width=800&height=1000&seq=about-marina-background-003&orientation=portrait"
                  alt="Marina Del Rey harbor"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Right - Content */}
            <div>
              <div className="flex items-center mb-4">
                <span className="w-2 h-2 bg-[#FF6B35] rounded-full mr-3"></span>
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">About Me</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Your Boat's<br />Trusted Guardian
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  I run The Boat Babysitter with the same discipline and common sense that carried me through years of producing high-stakes television. I've managed crews, budgets, tight deadlines, and the kind of chaos that teaches you how to stay calm and make smart decisions fast. Now I bring that same level of professionalism to something I love even more: caring for boats in Marina del Rey.
                </p>
                <p>
                  I live just a couple blocks from the marina, which means your vessel gets real oversight from someone who's present, attentive, and genuinely invested. I keep owners in the loop, handle the details they don't have time for, and make sure their boats stay ready for every arrival. Reliable, efficient, and easy to work with – that's the whole point.
                </p>
                <p className="font-semibold text-gray-900">
                  Your boat deserves someone who treats it like it matters. That's exactly what I do.
                </p>
              </div>
              <div className="mt-10 flex flex-wrap gap-4">
                <a 
                  href="#contact" 
                  className="px-8 py-4 bg-[#FF6B35] text-white font-semibold rounded-full hover:bg-[#E55A25] transition-all shadow-lg whitespace-nowrap cursor-pointer inline-flex items-center"
                >
                  Work With Me <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a 
                  href="tel:3237190661" 
                  className="px-8 py-4 border-2 border-gray-900 text-gray-900 font-semibold rounded-full hover:bg-gray-900 hover:text-white transition-all whitespace-nowrap cursor-pointer inline-flex items-center"
                >
                  <i className="ri-phone-line mr-2"></i> Call Me
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-16 mx-6 mb-6 rounded-[40px]">
        <div className="max-w-7xl mx-auto px-12">
          <div className="grid md:grid-cols-10 gap-12 mb-12">
            {/* Newsletter */}
            <div className="md:col-span-4">
              <h3 className="text-4xl font-light mb-4">Stay Updated</h3>
              <p className="text-gray-400 mb-6">Get seasonal boat care tips and MDR marina updates</p>
              <form className="flex flex-col sm:flex-row gap-4">
                <input 
                  type="email" 
                  placeholder="Your email"
                  className="flex-1 pb-4 border-b-2 border-white/30 focus:border-white outline-none text-lg transition-colors"
                />
                <button 
                  type="submit"
                  className="px-8 py-4 bg-white text-[#004E7C] font-semibold rounded-full hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
                >
                  Subscribe <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </form>
            </div>

            {/* Quick Links */}
            <div className="md:col-span-3">
              <h4 className="text-sm font-semibold mb-6 text-gray-400">Quick Links</h4>
              <ul className="space-y-3">
                <li><a href="#services" className="hover:text-[#FF6B35] transition-colors cursor-pointer underline">Services</a></li>
                <li><a href="#pricing" className="hover:text-[#FF6B35] transition-colors cursor-pointer underline">Pricing</a></li>
                <li><a href="#storage" className="hover:text-[#FF6B35] transition-colors cursor-pointer underline">Storage</a></li>
                <li><a href="tel:3237190661" className="hover:text-[#FF6B35] transition-colors cursor-pointer underline">Emergency</a></li>
                <li><a href="#contact" className="hover:text-[#FF6B35] transition-colors cursor-pointer underline">Contact</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div className="md:col-span-3">
              <h4 className="text-sm font-semibold mb-6 text-gray-400">Contact</h4>
              <ul className="space-y-3">
                <li><a href="tel:3237190661" className="hover:text-[#FF6B35] transition-colors cursor-pointer">Phone: (323) 719-0661</a></li>
                <li><a href="mailto:TheBoatBabysitter@gmail.com" className="hover:text-[#FF6B35] transition-colors cursor-pointer">TheBoatBabysitter@gmail.com</a></li>
                <li className="pt-4">
                  <p className="text-sm font-semibold mb-2 text-gray-400">Location</p>
                  <p className="text-gray-300">Marina Del Rey, CA<br />Serving all MDR slips</p>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex gap-6">
                <a href="#" className="hover:text-[#FF6B35] transition-colors cursor-pointer underline">Instagram</a>
                <a href="#" className="hover:text-[#FF6B35] transition-colors cursor-pointer underline">Facebook</a>
                <a href="#" className="hover:text-[#FF6B35] transition-colors cursor-pointer underline">Yelp</a>
              </div>
              <p className="text-gray-400 text-sm text-center">
                © 2025 The Boat Babysitter. Marina Del Rey's trusted boat care service.
              </p>
              <div className="flex gap-6">
                <a href="#" className="hover:text-[#FF6B35] transition-colors cursor-pointer underline text-sm">Privacy Policy</a>
                <a href="#" className="hover:text-[#FF6B35] transition-colors cursor-pointer underline text-sm">Terms</a>
                <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF6B35] transition-colors cursor-pointer underline text-sm">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
